// app/src/lib/procurement/vendors.js — Vendor Master (US-2.1, US-2.2).
//
// Includes:
//   - create / update / softDelete (deactivate) / merge
//   - performance metrics over a rolling 90-day window (fill rate, short rate,
//     on-time rate, price variance) sourced from three-way match data when
//     available.
//   - alias management for OCR vendor name → vendorId resolution.

import { db } from '../db.js';
import { requireUserId, normalize } from './util.js';
import { mutate } from '../scale/instrument.js';

/** @typedef {{ vendorId:string, userId:string, name:string, aliases:string[], normalizedName:string, accountNumber?:string, repName?:string, repEmail?:string, repPhone?:string, paymentTerms?:string, deliveryDays?:string[], minimumOrderCents?:number, isActive:number, tags?:string[], notes?:string }} Vendor */

const MIN_DATA_THRESHOLD = 3;
const PERF_WINDOW_DAYS = 90;

/**
 * Create a vendor. The optional `existingId` allows the OCR flow to create the
 * vendor synchronously when a new name is confirmed.
 * @param {Partial<Vendor>} input
 * @returns {Promise<Vendor>}
 */
export async function create(input) {
  const userId = await requireUserId();
  if (!input.name) throw new Error('vendor.create: name required');
  /** @type {Vendor} */
  const vendor = {
    vendorId: input.vendorId || crypto.randomUUID(),
    userId,
    name: input.name,
    aliases: dedupeStringArr(input.aliases),
    normalizedName: normalize(input.name),
    accountNumber: input.accountNumber,
    repName: input.repName,
    repEmail: input.repEmail,
    repPhone: input.repPhone,
    paymentTerms: input.paymentTerms,
    deliveryDays: dedupeStringArr(input.deliveryDays),
    minimumOrderCents: input.minimumOrderCents,
    isActive: 1,
    tags: dedupeStringArr(input.tags),
    notes: input.notes,
  };
  return mutate(
    { entityType: 'vendor', entityId: vendor.vendorId, action: 'create', after: vendor },
    async () => { await db.vendors.put(vendor); return vendor; },
  );
}

/** Patch any subset of editable fields. */
export async function update(vendorId, patch) {
  const userId = await requireUserId();
  const existing = await db.vendors.get(vendorId);
  if (!existing || existing.userId !== userId) throw new Error('vendor.update: not found');
  const next = {
    ...existing,
    ...patch,
    aliases: patch.aliases ? dedupeStringArr(patch.aliases) : existing.aliases,
    tags: patch.tags ? dedupeStringArr(patch.tags) : existing.tags,
    normalizedName: patch.name ? normalize(patch.name) : existing.normalizedName,
  };
  return mutate(
    { entityType: 'vendor', entityId: vendorId, action: 'update', before: existing, after: next },
    async () => { await db.vendors.put(next); return next; },
  );
}

/** Soft-delete: hide from ordering UI but retain history (US-2.1). */
export async function deactivate(vendorId) {
  return update(vendorId, { isActive: 0 });
}

export async function reactivate(vendorId) {
  return update(vendorId, { isActive: 1 });
}

/**
 * Merge `srcId` into `dstId`. Moves invoices, line-item references, vendorItems,
 * priceEntries, vendorContracts, deliverySlips, and purchaseOrders to `dstId`.
 * Adds `src.name` and `src.aliases` to dst's aliases. Deletes src.
 *
 * Idempotent: re-running with the same args is a no-op after the first call.
 */
export async function merge(srcId, dstId) {
  const userId = await requireUserId();
  if (srcId === dstId) return;
  const [src, dst] = await Promise.all([db.vendors.get(srcId), db.vendors.get(dstId)]);
  if (!src || src.userId !== userId) throw new Error('vendor.merge: src not found');
  if (!dst || dst.userId !== userId) throw new Error('vendor.merge: dst not found');

  // Route through mutate() so the merge (a) is blocked on read-only devices,
  // (b) is audited, and — critically — (c) marks the books dirty so the shared
  // snapshot is uploaded. Without this the deletion stays local and never syncs,
  // so the duplicate reappears on the next pull / on other devices.
  return mutate(
    { entityType: 'vendor', entityId: srcId, action: 'delete', before: src, meta: { mergedInto: dstId } },
    async () => {
      await db.transaction('rw',
        db.vendors, db.invoices, db.vendorItems, db.priceEntries,
        db.vendorContracts, db.deliverySlips, db.purchaseOrders, db.parItems,
        async () => {
          const reassign = async (table, idxName) => {
            const rows = await table.where(idxName).equals(srcId).toArray();
            for (const r of rows) { r.vendorId = dstId; await table.put(r); }
          };
          await Promise.all([
            reassign(db.invoices, 'vendorId'),
            reassign(db.vendorItems, 'vendorId'),
            reassign(db.priceEntries, 'vendorId'),
            reassign(db.vendorContracts, 'vendorId'),
            reassign(db.deliverySlips, 'vendorId'),
            reassign(db.purchaseOrders, 'vendorId'),
            // parItems uses preferredVendorId.
            (async () => {
              const rows = await db.parItems.where('preferredVendorId').equals(srcId).toArray();
              for (const r of rows) { r.preferredVendorId = dstId; await db.parItems.put(r); }
            })(),
          ]);

          const merged = {
            ...dst,
            aliases: dedupeStringArr([...(dst.aliases || []), src.name, ...(src.aliases || [])]),
          };
          await db.vendors.put(merged);
          await db.vendors.delete(srcId);
        },
      );
      // Clear the merged-away vendor's "new vendor — set its terms" nudge so it
      // doesn't linger on Today pointing at a vendor that no longer exists.
      const stale = await db.alerts.where('userId').equals(userId)
        .filter((a) => a.kind === 'NEW_VENDOR' && a.meta && a.meta.vendorId === srcId && !a.dismissed)
        .toArray();
      for (const a of stale) { a.dismissed = 1; await db.alerts.put(a); }
    },
  );
}

/** Find a vendor by name or any known alias. Returns null if not found. */
export async function findByName(name) {
  const userId = await requireUserId();
  const n = normalize(name);
  if (!n) return null;
  // Fast path: normalizedName equality.
  const byName = await db.vendors
    .where('[userId+name]').between([userId, '\u0000'], [userId, '￿'])
    .filter((v) => normalize(v.name) === n || (v.aliases || []).some((a) => normalize(a) === n))
    .first();
  return byName || null;
}

/** List active vendors for the current user. */
export async function listActive() {
  const userId = await requireUserId();
  return db.vendors.where('[userId+isActive]').equals([userId, 1]).toArray();
}

/**
 * Active vendors ordered the way a picker wants them: whoever you bought from
 * most recently first, so the supplier you're holding an invoice from is near
 * the top of the list. Vendors with no invoices yet (added by hand, or arriving
 * from Contacts) still appear — just after the ones with history.
 *
 * This is the list behind "choose a supplier" on manual entry. Picking from it
 * binds the invoice to a vendorId, which is what keeps a typo from silently
 * opening a second vendor for a supplier already on the books.
 *
 * @returns {Promise<Array<{ vendorId:string, name:string, aliases:string[], invoiceCount:number, lastDate:string|null }>>}
 */
export async function listForPicker() {
  const userId = await requireUserId();
  const [vendors, invoices] = await Promise.all([
    listActive(),
    db.invoices.where('userId').equals(userId).toArray(),
  ]);
  const stat = {};
  for (const i of invoices) {
    if (!i.vendorId || i.isDuplicate) continue;
    const s = stat[i.vendorId] || (stat[i.vendorId] = { count: 0, lastDate: '' });
    s.count += 1;
    if ((i.issuedDate || '') > s.lastDate) s.lastDate = i.issuedDate || '';
  }
  return vendors
    .map((v) => ({
      vendorId: v.vendorId,
      name: v.name,
      // Carried so the picker can match an OCR'd name against learned spellings
      // ("US FOODS | CHEF'S STORE" → "US Chef's Store"), same as the save path.
      aliases: v.aliases || [],
      invoiceCount: stat[v.vendorId]?.count || 0,
      lastDate: stat[v.vendorId]?.lastDate || null,
    }))
    .sort((a, b) =>
      (b.lastDate || '').localeCompare(a.lastDate || '')
      || b.invoiceCount - a.invoiceCount
      || (a.name || '').localeCompare(b.name || ''));
}

/**
 * Performance metrics for a vendor over the rolling window.
 *
 * @returns {Promise<{
 *   sampleSize: number,
 *   sufficient: boolean,
 *   fillRate: number|null,
 *   shortRate: number|null,
 *   onTimeRate: number|null,
 *   priceVariancePct: number|null,
 *   totalSpendCents: number,
 *   lastOrderDate: string|null,
 *   openInvoices: number,
 * }>}
 */
export async function performanceMetrics(vendorId) {
  const userId = await requireUserId();
  const v = await db.vendors.get(vendorId);
  if (!v || v.userId !== userId) throw new Error('vendor: not found');

  const cutoff = new Date(); cutoff.setUTCDate(cutoff.getUTCDate() - PERF_WINDOW_DAYS);
  const cutoffIso = cutoff.toISOString().slice(0, 10);

  // Invoices in the window for spend + last order + open count.
  // userId filter is defense-in-depth: vendorId is already owner-verified above,
  // but a corrupt restore could conceivably leave cross-user rows in the table.
  const invoices = await db.invoices
    .where('[vendorId+issuedDate]')
    .between([vendorId, cutoffIso], [vendorId, '￿'])
    .filter((i) => i.userId === userId)
    .toArray();

  const totalSpendCents = invoices.reduce((s, i) => s + (i.totalCents || 0), 0);
  const lastOrderDate = invoices.reduce((max, i) => i.issuedDate > max ? i.issuedDate : max, '');
  const openInvoices = invoices.filter((i) => i.status === 'RECEIVED' || i.status === 'PAID_LOCAL').length;

  // Three-way-match-derived rates: look at delivery slips linked to invoices in window.
  const slips = await db.deliverySlips
    .where('[vendorId+deliveryDate]')
    .between([vendorId, cutoffIso], [vendorId, '￿'])
    .filter((s) => s.userId === userId)
    .toArray();

  let filled = 0, ordered = 0, ordersWithShorts = 0, onTime = 0;
  for (const slip of slips) {
    const slipLines = await db.deliveryLineItems.where('slipId').equals(slip.slipId).toArray();
    let hasShort = false;
    for (const ln of slipLines) {
      const qOrd = Number(ln.quantityOrdered) || 0;
      const qDel = Number(ln.quantityDelivered) || 0;
      ordered += qOrd;
      filled += Math.min(qOrd, qDel);
      if (qDel < qOrd) hasShort = true;
    }
    if (hasShort) ordersWithShorts += 1;
    if (slip.expectedDeliveryDate) {
      const slipDate = slip.deliveryDate;
      const expected = slip.expectedDeliveryDate;
      const diff = Math.abs(daysDiff(slipDate, expected));
      if (diff <= 1) onTime += 1;
    }
  }

  // Price variance: only computed for line items where a contract exists.
  let variancePctSum = 0, variancePctCount = 0;
  for (const inv of invoices) {
    const lineItems = await db.lineItems.where('invoiceId').equals(inv.invoiceId).toArray();
    for (const li of lineItems) {
      if (!li.itemId) continue;
      const contract = await activeContract(vendorId, li.itemId, inv.issuedDate);
      if (!contract) continue;
      if (contract.agreedPriceCents <= 0) continue;
      const variance = (li.unitPriceCents - contract.agreedPriceCents) / contract.agreedPriceCents;
      variancePctSum += variance;
      variancePctCount += 1;
    }
  }

  const sampleSize = slips.length;
  const sufficient = sampleSize >= MIN_DATA_THRESHOLD;
  return {
    sampleSize,
    sufficient,
    fillRate: ordered > 0 ? filled / ordered : null,
    shortRate: sampleSize > 0 ? ordersWithShorts / sampleSize : null,
    onTimeRate: sampleSize > 0 ? onTime / sampleSize : null,
    priceVariancePct: variancePctCount > 0 ? variancePctSum / variancePctCount : null,
    totalSpendCents,
    lastOrderDate: lastOrderDate || null,
    openInvoices,
  };
}

async function activeContract(vendorId, itemId, asOfIso) {
  const candidates = await db.vendorContracts
    .where('[vendorId+itemId]').equals([vendorId, itemId])
    .toArray();
  return candidates.find((c) => c.effectiveFrom <= asOfIso && c.effectiveThrough >= asOfIso) || null;
}

function daysDiff(a, b) {
  return Math.round((new Date(a) - new Date(b)) / (24 * 60 * 60 * 1000));
}

function dedupeStringArr(arr) {
  if (!Array.isArray(arr)) return [];
  return Array.from(new Set(arr.filter((s) => typeof s === 'string' && s.length > 0)));
}
